import * as React from "react";
import * as ReactDOM from "react-dom";

// React 17 ships flushSync, but @types/react-dom 17.0.0 doesn't declare it.
const { flushSync } = ReactDOM as unknown as { flushSync: (update: () => void) => void };

export type Theme = "light" | "dark";
/** Older names, kept for existing callers. */
export type ThemePreference = Theme;
export type ResolvedTheme = Theme;

// public/init.js reads the same key before React mounts; keep the two in sync. Light is the
// default; once signed in, the account's choice (user.themePreference) wins (see ThemeSync).
const STORAGE_KEY = "sap-theme";
// Slow enough to watch the new theme sweep across the page.
const REVEAL_MS = 1200;

/** Where a theme change was asked for, so the new theme can grow out of that point. */
export interface ThemeOrigin {
  x: number;
  y: number;
}

interface ThemeContextValue {
  theme: Theme;
  /** Same as `theme` (there is no "follow the system" mode any more). */
  resolvedTheme: Theme;
  preference: Theme;
  setTheme: (theme: Theme, origin?: ThemeOrigin) => void;
  setPreference: (theme: Theme, origin?: ThemeOrigin) => void;
  toggleTheme: (origin?: ThemeOrigin) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

const readTheme = (): Theme => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch (err) {
    return "light";
  }
};

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void> };
};

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = React.useState<Theme>(readTheme);

  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.classList.toggle("light", theme === "light");
    // The browser's own UI (the address bar on phones) follows the app's theme, not the OS.
    const background = getComputedStyle(root).getPropertyValue("--background").trim();
    if (background) {
      document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
        meta.setAttribute("content", `rgb(${background})`);
        meta.removeAttribute("media");
      });
    }
  }, [theme]);

  const setTheme = React.useCallback((next: Theme, origin?: ThemeOrigin) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      // Storage blocked: the choice still applies for this session.
    }
    const doc = document as ViewTransitionDocument;
    if (!origin || !doc.startViewTransition || prefersReducedMotion()) {
      setThemeState(next);
      return;
    }
    // The new theme grows as a circle from the switch that was pressed (View Transitions API;
    // browsers without it just swap).
    const transition = doc.startViewTransition(() => {
      flushSync(() => setThemeState(next));
    });
    transition.ready
      .then(() => {
        const radius = Math.hypot(
          Math.max(origin.x, window.innerWidth - origin.x),
          Math.max(origin.y, window.innerHeight - origin.y)
        );
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${origin.x}px ${origin.y}px)`,
              `circle(${radius}px at ${origin.x}px ${origin.y}px)`,
            ],
          },
          {
            duration: REVEAL_MS,
            easing: "cubic-bezier(0.65, 0, 0.35, 1)",
            pseudoElement: "::view-transition-new(root)",
          } as KeyframeAnimationOptions
        );
      })
      .catch(() => undefined);
  }, []);

  const toggleTheme = React.useCallback(
    (origin?: ThemeOrigin) => setTheme(theme === "dark" ? "light" : "dark", origin),
    [theme, setTheme]
  );

  const value = React.useMemo(
    () => ({
      theme,
      resolvedTheme: theme,
      preference: theme,
      setTheme,
      setPreference: setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
